'use client'

import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Icons } from '@/components/ui/icons'
import { Badge } from '@/components/ui/badge'

interface Notification {
  id: string
  roomName: string
  dealerName: string
  dealerLocation: string
  reason: string
  currentEmotion: string
  timestamp: number
  dismissed: boolean
}

export function TSODashboard() {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadNotifications()
    
    // Auto-refresh every 5 seconds
    const interval = setInterval(loadNotifications, 5000)
    return () => clearInterval(interval)
  }, [])

  const loadNotifications = async () => {
    try {
      const response = await fetch('/api/video-call/tso/notifications')
      if (response.ok) {
        const data = await response.json()
        setNotifications(data.notifications || [])
      }
    } catch (error) {
      console.error('Failed to load notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleJoinCall = async (notification: Notification) => {
    try {
      const response = await fetch('/api/video-call/tso/join-room', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomName: notification.roomName,
          tsoName: 'Territory Sales Officer',
        }),
      })

      if (response.ok) {
        const data = await response.json()
        // Open video call in new tab
        const params = new URLSearchParams({
          token: data.token,
          room: notification.roomName,
          name: 'TSO',
          isTSO: 'true',
        })
        window.open(`/video-call?${params.toString()}`, '_blank')
      }
    } catch (error) {
      console.error('Failed to join call:', error)
    }
  }

  const handleDismiss = async (notificationId: string) => {
    try {
      await fetch('/api/video-call/tso/dismiss-notification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notificationId }),
      })
      loadNotifications()
    } catch (error) {
      console.error('Failed to dismiss notification:', error)
    }
  }

  const activeNotifications = notifications.filter(n => !n.dismissed)
  const getEmotionColor = (emotion: string) => {
    const colors: Record<string, string> = {
      angry: 'bg-red-500',
      frustrated: 'bg-orange-500',
      happy: 'bg-green-500',
      neutral: 'bg-gray-500',
      sad: 'bg-blue-500',
    }
    return colors[emotion] || 'bg-gray-500'
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-cornflower/10 via-brand-sky/10 to-brand-periwinkle/10">
      {/* Header */}
      <div className="border-b bg-white/70 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="flex items-center gap-2 text-2xl font-bold text-brand-navy">
                <Icons.users className="h-6 w-6" />
                TSO Dashboard
              </h1>
              <p className="text-sm text-muted-foreground">
                Adani Cement - Sales Support
              </p>
            </div>
            
            <div className="flex gap-4">
              <Card>
                <CardContent className="flex flex-col items-center gap-1 p-4">
                  <div className="text-3xl font-bold text-brand-navy">
                    {activeNotifications.length}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    Active
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="flex flex-col items-center gap-1 p-4">
                  <div className="text-3xl font-bold text-brand-navy">
                    {notifications.length}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    Total Today
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-8">
        {loading ? (
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <Icons.loader className="mb-4 h-12 w-12 animate-spin text-brand-cornflower mx-auto" />
              <p className="text-muted-foreground">Loading notifications...</p>
            </div>
          </div>
        ) : activeNotifications.length === 0 ? (
          <Card className="mx-auto max-w-2xl">
            <CardContent className="flex flex-col items-center gap-4 py-12">
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-brand-cornflower/10">
                <Icons.bell className="h-12 w-12 text-brand-cornflower" />
              </div>
              <div className="text-center">
                <h2 className="mb-2 text-xl font-semibold">No Active Requests</h2>
                <p className="text-sm text-muted-foreground">
                  When dealers request assistance, notifications will appear here.
                </p>
              </div>
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icons.refresh className="h-4 w-4 animate-spin" />
                Auto-refreshing every 5 seconds...
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {activeNotifications.map((notification) => (
              <Card key={notification.id} className="border-l-4 border-l-red-500">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{notification.dealerName}</CardTitle>
                      <CardDescription>📍 {notification.dealerLocation}</CardDescription>
                    </div>
                    <Badge className={getEmotionColor(notification.currentEmotion)}>
                      {notification.currentEmotion}
                    </Badge>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Room:</span>
                      <span className="font-mono text-xs">{notification.roomName.slice(-8)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Reason:</span>
                      <span>{notification.reason.replace('_', ' ')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Time:</span>
                      <span>{new Date(notification.timestamp).toLocaleTimeString()}</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleJoinCall(notification)}
                      className="flex-1 gap-2"
                    >
                      <Icons.video className="h-4 w-4" />
                      Join Call
                    </Button>
                    <Button
                      onClick={() => handleDismiss(notification.id)}
                      variant="ghost"
                      size="icon"
                    >
                      <Icons.check className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
